import WasmController from "react-lib/frameworks/WasmController";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";

// Common
import { SetProperty } from "../../common/CommonInterface";
import moment from "moment";

// Utils
import { dateToStringWithoutTimeBE } from "react-lib/utils/dateUtils";

// Serializer
import DFInterfaceSerializer from "issara-sdk/types/DFInterfaceSerializer_apps_INF";

// API
import DFInterfaceList from "issara-sdk/apis/DFInterfaceList_apps_INF";
import DFInterfaceUpdateView from "issara-sdk/apis/DFInterfaceUpdateView_apps_INF";
import UserList from "issara-sdk/apis/UserList_users";

export type Props = {

}

export type State = Partial<{
  // seq
  DFInterfaceSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    dfInterfaceList: DFInterfaceSerializer[];
    selectedItems: SelectedItemsType[];
    userOptions: any[];
    searchDF: FilterSearchType;
    paginatedItems: PaginatedItemsType;
  }> | null;
}>;

export type SelectedItemsType = Partial<{
  id: number;
  df_type: string;
  sel_date: string;
  per_unit: string;
  per_na: string;
  req_unit: string;
  req_na: string;
}>;

export type FilterSearchType = Partial<{
  createdDate: string | null;
  hn: string | null;
  serviceId: string | null;
  productId: string | null;
  bilDocNo: string | null;
}>;

type PaginatedItemsType = Partial<{
  items: DFInterfaceSerializer[];
  page: number;
  pageSize: number;
  total: number;
}>;

type PickedState = Partial<Pick<MainState, "buttonLoadCheck" | "errorMessage" | "successMessage">>;

export type PickedProps = Partial<Omit<PickedState, "">>;

// Sequence
type SeqState = {
  sequence: "DFInterface";
  restart?: boolean;
  clear?: boolean;
};

export type SaveType = "ADD" | "EDIT";

export type TableHeaderType<T> = {
  Header: string;
  accessor: keyof T;
  width?: number;
};

export type AccessorType = Partial<Record<keyof DFInterfaceSerializer, string>>;

export type WidthsType = Partial<Record<keyof DFInterfaceSerializer, number>>;

// Handle Action
type ActionType =
  | { action: "SEARCH"; cardKey: string; page?: number; pageSize?: number }
  | { action: "CLEAR"; cardKey: string }
  | { action: "SAVE"; cardKey: string; type: SaveType; selectedItems: any[] };

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;

export type SetProp = SetProperty<State & PickedState>;

type CustomExtract<T, U> = T extends object ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const StateInitial: State & PickedState = {
  // seq
  DFInterfaceSequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  successMessage: null,
  errorMessage: null,
};

export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
};

export const DataInitial = {};

export const CARD_NAME = "CardDFInterface";

type Optional<T> = {
  [K in keyof T]: `${typeof CARD_NAME}_${T[K] & string}`;
};

export const ACTIONS = {
  SEARCH: "SEARCH",
  CLEAR: "CLEAR",
  SAVE: "SAVE",
} as const;

export const BTN_ACTS = Object.fromEntries(
  Object.keys(ACTIONS).map((key) => [key, `${CARD_NAME}_${key}`])
) as Optional<typeof ACTIONS>;

const FILTER_INIT: FilterSearchType = {
  hn: "",
  serviceId: "",
  productId: "",
  bilDocNo: "",
};

export const LIST_LIMIT = 20;

const PAGINATED_INIT: PaginatedItemsType = {
  items: [],
  page: 0,
  total: 0,
  pageSize: LIST_LIMIT,
};

type Handler<P = any, R = any> = (
  controller: WasmController<State & PickedState, Event, Data, Props>,
  params: P
) => R;

/**========================================================================
 **                               START
 *========================================================================**/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  const currentDate = dateToStringWithoutTimeBE(moment());

  controller.setState(
    {
      DFInterfaceSequence: {
        ...state.DFInterfaceSequence,
        sequenceIndex: "Action",
        searchDF: {
          createdDate: currentDate,
        },
        paginatedItems: {
          pageSize: LIST_LIMIT,
          page: 0,
        },
      },
    },
    // () => Action(controller, { ...params, action: "SEARCH", cardKey: CARD_NAME })
  );
};

/**========================================================================
 **                              ACTIONS
 *========================================================================**/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers = {
    [ACTIONS.SEARCH]: HandleSearchData,
    [ACTIONS.CLEAR]: HandleClearData,
    [ACTIONS.SAVE]: HandleSaveData,
  };

  const action = params.action;

  if (action && actionHandlers[action]) {
    return actionHandlers[action](controller, params as any);
  }
};

const HandleSearchData: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  const searchDF = state.DFInterfaceSequence?.searchDF;
  const paginatedItems = state.DFInterfaceSequence?.paginatedItems;
  const page = (params?.page ?? paginatedItems?.page) || 0;
  const pageSize = (params?.pageSize ?? paginatedItems?.pageSize) || LIST_LIMIT;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: "LOADING",
    },
  });

  const offset = page * pageSize;

  let data = {
    limit: pageSize,
    offset,
    create_date: searchDF?.createdDate,
    hn: searchDF?.hn,
    service_id: searchDF?.serviceId,
    product_id: searchDF?.productId,
    bil_doc_no: searchDF?.bilDocNo,
  };

  const result = await GetDFInterfaceData(controller, data);

  if (result?.items) {
    const uniqueData = (result.items || [])
      .filter(
        (item: DFInterfaceSerializer, index: number, self: any) =>
          index === self.findIndex((t: any) => t.created_by === item.created_by)
      )
      .map((item: DFInterfaceSerializer) => item?.created_by);

    const userOptions = await Promise.all(
      uniqueData.map(async (id: number) => {
        const user = await GetUserList(controller, { id });

        return user;
      })
    );

    controller.setState({
      DFInterfaceSequence: {
        ...state.DFInterfaceSequence,
        dfInterfaceList: result?.items,
        userOptions: userOptions,
        paginatedItems: {
          ...paginatedItems,
          page,
          pageSize,
          total: result?.total,
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "SUCCESS",
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "ERROR",
      },
    });
  }
};

const HandleSaveData: Handler<Params<"SAVE">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${CARD_NAME}_${params.type}`]: "LOADING",
    },
  });

  const [saveResp, saveErr] = await SaveDFInterface(controller, {
    type: params.type,
    selectedItems: params.selectedItems,
  });

  if (saveResp) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${CARD_NAME}_${params.type}`]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [CARD_NAME]: "The system has successfully",
      },
    });

    HandleSearchData(controller, { action: "SEARCH", cardKey: CARD_NAME });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${CARD_NAME}_${params.type}`]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: saveErr,
      },
    });
  }
};

const HandleClearData: Handler<Params<"CLEAR">> = async (controller, params) => {
  const state = controller.getState();

  const currentDate = dateToStringWithoutTimeBE(moment());

  controller.setState({
    DFInterfaceSequence: {
      ...state.DFInterfaceSequence,
      searchDF: {
        ...FILTER_INIT,
        createdDate: currentDate,
      },
      dfInterfaceList: [],
      paginatedItems: {
        ...PAGINATED_INIT,
      },
    },
  });
};

/**========================================================================
 **                               APIs
 *========================================================================**/

const GetDFInterfaceData: Handler = async (controller, params) => {
  const state = controller.getState();

  /* params: 'create_date', 'product_code', 'source_storage_code', 'destination_storage_code', 'vendor_code' */
  const [resp, err] = await DFInterfaceList.list({
    apiToken: controller.apiToken,
    params,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const GetUserList: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await UserList.list({
    apiToken: controller.apiToken,
    params: { pk: params.id },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp?.items?.[0];
};

const SaveDFInterface: Handler = async (controller, params) => {
  const state = controller.getState();

  const apiType = params.type === "ADD" ? DFInterfaceUpdateView.post : DFInterfaceUpdateView.put;

  const [resp, err] = await apiType({
    apiToken: controller.apiToken,
    data: {
      item_data_list: params?.selectedItems || [],
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

/**========================================================================
 **                               Utils
 *========================================================================**/

export const accessorList: Partial<Record<keyof DFInterfaceSerializer, string>> = {
  id: "ID",
  valid: "Valid",
  error_message: "Error Message",
  created_by: "User ที่สร้าง",
  created_at: "วันเวลาที่สร้าง",
  sent: "Sent",
  file_name: "File Name",
  version: "Version",
  invoice_item_status: "Invoice Item Status",
  data_type: "Data Type",
  sel_date: "Sel Date",
  rec_date: "Rec Date",
  rec_no: "Rec No",
  hn: "HN",
  service_id: "Service ID",
  qty: "Qty",
  df_type: "DF Type",
  amt: "AMT",
  price: "Price",
  per_unit: "Per Unit",
  per_unit_old: "Per Unit Old",
  per_date: "Per Date",
  per_time: "Per Time",
  perf_pid: "Perf PID",
  perf_na: "Perf NA",
  perf_role: "Perf Role",
  role_code: "Role Code",
  compensate: "Compensate",
  product_id: "Product ID",
  sac_type: "SAC Type",
  bil_doc_no: "Bil Doc No",
  sithi: "สิทธิ",
  req_unit: "Req Unit",
  req_na: "Req NA",
  frmtbl: "FRMTBL",
  per_na: "Per NA",
  prod_na: "Prod NA",
  _id: "ID",
  obs_type: "OBS Type",
  obs_id: "OBS ID",
  obs_seq: "OBD Seq",
  status: "Status",
  bill_type: "Bill Type",
  remark: "Remark",
  doctor_fee_order: "Doctor Fee Order",
  service_compensation_item: "Service Compensation",
  invoice_item: "Invoice Item",
};

export const GenerateTableHeader = (
  columnnWidths?: WidthsType
): TableHeaderType<DFInterfaceSerializer>[] => {
  return (Object.entries(accessorList) as [keyof DFInterfaceSerializer, string][]).map(
    ([key, label]) => ({
      Header: label,
      accessor: key,
      ...(columnnWidths && columnnWidths[key] ? { width: columnnWidths[key] } : {}),
    })
  );
};
